import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FormInlineFormField, FormRadioField } from "../../../../actions/Tenants/config/constantsTyped";
import { Form, FormOptions } from "../PageBase";
import { convertEmailRecipientsObjectIntoTextList, convertEmailRecipientTextListIntoObject, createEmailRecipientsField, EmailRecipientsTextList, getInfoFormFields } from "../utils";
import { SupplierInfo } from "../Supplier/types";
import { ClientInfo } from "../Client/types";
import { EntityState } from "../../../../actions/Admin/reducer";

export interface InternalEmailFormObject {
    fruit_type?: string | null;
    report_type?: string | null;
    report_status?: string | null;
    email_recipients: EmailRecipientsTextList;
    id?: string;
}

export type InternalEmailsState = EntityState<SupplierInfo>;

export const defineInternalEmailsFormCreate = (onSaveInternalEmails: (info: SupplierInfo | ClientInfo) => void, onDeleteInternalEmails:(id:string)=>void) => (formOptions: FormOptions | undefined, currentEntity: SupplierInfo, modalObject: InternalEmailFormObject, state: InternalEmailsState) => {
    const supplierInfoForm = getInfoFormFields(formOptions, currentEntity);
    const [formType, setFormType] = useState<"edit" | "add">();

    const reportStatusField: FormRadioField = {
        label: "Report status",
        name: "report_status",
        type: "radio",
        options: [
            { value: "*", label: "All" },
            { value: "DRAFT", label: "Draft" },
            { value: "READY", label: "Ready" },
        ],
        default_value: "*",
    };

    const emailsField: FormInlineFormField = {
        label: "Email",
        name: "email_recipients",
        type: "inline-form",
        fields: [
            createEmailRecipientsField("Cc", "Cc"),
        ],
    };

    const internalEmailsForm = useMemo(() => {
        if (formType === "edit") {
            return [emailsField];
        }

        if (formType === "add") {
            return [
                supplierInfoForm[0],
                supplierInfoForm[1],
                reportStatusField,
                emailsField
            ];
        }

        return [];
    }, [formType, supplierInfoForm]);

    const getFormObjectFromEntity = (entity: Partial<SupplierInfo>): Partial<InternalEmailFormObject> => {
        return {
            ...entity,
            email_recipients: convertEmailRecipientsObjectIntoTextList(entity.email_recipients),
        };
    };

    const onPreEditModalOpen = () => {
        setFormType("edit");
    };

    const onPreAddModalOpen = () => {
        setFormType("add");
    };

    const onSave = (formObject: InternalEmailFormObject, entity: Partial<SupplierInfo>) => {
        const fruit_type = formObject.fruit_type === "*" ? undefined : formObject.fruit_type;
        const report_type = formObject.report_type === "*" ? undefined : formObject.report_type;
        const report_status = formObject.report_status === "*" ? undefined : formObject.report_status;
        const email_recipients = convertEmailRecipientTextListIntoObject(formObject.email_recipients);

        const internalEmailsEntity = {
            ...entity,
            fruit_type,
            report_type,
            report_status,
            email_recipients,
        };

        // check if there is already an entity with the same fruit type
        const existingEntity = state.items.results.find(
            (item) => item.fruit_type === internalEmailsEntity.fruit_type
                    && item.report_type === internalEmailsEntity.report_type
                    && item.report_status === internalEmailsEntity.report_status
                    && item.id !== internalEmailsEntity.id
        );

        if (existingEntity) {
            toast.error("Internal emails already exists for this fruit type, report type and report status combination");
        } else {
            onSaveInternalEmails(internalEmailsEntity as SupplierInfo);
        }
    };

    const onDelete = (formObject: InternalEmailFormObject, entity: Partial<SupplierInfo>) => {
        onDeleteInternalEmails(entity.id!);
    };

    const editModal: Form<InternalEmailFormObject, SupplierInfo> = {
        title: "Internal emails",
        fields: internalEmailsForm,
        modalButtonLabel: "Edit",
        onSave,
        onDelete,
        getFormObjectFromEntity,
        onPreEditModalOpen,
        onPreAddModalOpen
    };

    return editModal;
};
